import React from 'react';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';

const CurrentBookingDetails = ({ booking }) => {
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date?.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
  };

  const calculateNights = () => {
    const checkIn = new Date(booking?.checkIn);
    const checkOut = new Date(booking?.checkOut);
    const diffTime = Math.abs(checkOut - checkIn);
    return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  };

  return (
    <div className="bg-card rounded-xl border border-border overflow-hidden shadow-lg sticky top-24">
      {/* Header */}
      <div className="bg-gradient-to-r from-primary to-secondary p-4">
        <h2 className="text-lg font-bold text-white flex items-center gap-2">
          <Icon name="FileText" size={20} />
          Current Booking
        </h2>
      </div>

      {/* Booking Image */}
      {booking?.image && (
        <div className="relative h-48 w-full">
          <Image
            src={booking?.image}
            alt={booking?.imageAlt || booking?.name}
            className="w-full h-full object-cover"
          />
        </div>
      )}

      {/* Details */}
      <div className="p-4 space-y-4">
        <div>
          <h3 className="text-xl font-bold text-foreground mb-1">{booking?.name}</h3>
          <div className="flex items-center gap-2 text-muted-foreground">
            <Icon name="MapPin" size={16} />
            <span className="text-sm">{booking?.location}</span>
          </div>
        </div>

        {/* Dates */}
        <div className="space-y-3">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
              <Icon name="Calendar" size={18} className="text-primary" />
            </div>
            <div className="flex-1">
              <p className="text-xs text-muted-foreground mb-1">Check-in</p>
              <p className="text-sm font-semibold text-foreground">{formatDate(booking?.checkIn)}</p>
            </div>
          </div>

          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-lg bg-secondary/10 flex items-center justify-center flex-shrink-0">
              <Icon name="Calendar" size={18} className="text-secondary" />
            </div>
            <div className="flex-1">
              <p className="text-xs text-muted-foreground mb-1">Check-out</p>
              <p className="text-sm font-semibold text-foreground">{formatDate(booking?.checkOut)}</p>
            </div>
          </div>

          <div className="p-3 bg-accent/10 rounded-lg border border-accent/20">
            <div className="flex items-center justify-between">
              <span className="text-sm text-foreground">Duration</span>
              <span className="text-sm font-semibold text-accent">
                {calculateNights()} {calculateNights() === 1 ? 'Night' : 'Nights'}
              </span>
            </div>
          </div>
        </div>

        {/* Guests */}
        <div className="flex items-center gap-3 p-3 bg-muted/50 rounded-lg">
          <Icon name="Users" size={18} className="text-primary" />
          <div className="flex-1">
            <p className="text-xs text-muted-foreground">Guests</p>
            <p className="text-sm font-semibold text-foreground">
              {booking?.guests} {booking?.guests === 1 ? 'Guest' : 'Guests'}
            </p>
          </div>
        </div>

        {/* Current Price */}
        <div className="pt-4 border-t border-border">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm text-muted-foreground">Current Total</span>
            <span className="text-2xl font-bold text-primary">
              ₱{booking?.amount?.toLocaleString('en-PH', { minimumFractionDigits: 2 })}
            </span>
          </div>
        </div>

        {/* Status Badges */}
        <div className="flex flex-wrap gap-2">
          <div className="px-3 py-1 bg-success/10 text-success text-xs font-medium rounded-full border border-success/20">
            {booking?.bookingStatus}
          </div>
          <div className="px-3 py-1 bg-warning/10 text-warning text-xs font-medium rounded-full border border-warning/20">
            {booking?.paymentStatus}
          </div>
        </div>

        {/* Cancellation Policy */}
        <div className="p-3 bg-muted/50 rounded-lg border border-border">
          <div className="flex items-start gap-2">
            <Icon name="Info" size={16} className="text-primary mt-0.5 flex-shrink-0" />
            <div>
              <p className="text-xs font-semibold text-foreground mb-1">Modification Policy</p>
              <p className="text-xs text-muted-foreground">
                {booking?.cancellationPolicy || 'Free modifications until 7 days before check-in. Fees may apply for changes within 7 days.'}
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CurrentBookingDetails;